import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, untracked } from '@angular/core'
import { SwissQrBillCurrency } from '@pai/commons'
import { firstValueFrom, Subject } from 'rxjs'

import { AnalyticsService } from 'app/core/analytics/analytics.service'
import { AnalyticsEvent } from 'app/core/analytics/analytics-event.enum'
import { CLIENT_CONFIG } from 'app/core/client-config/client-config.token'
import { CardSwissBillQrCodeDonation } from 'app/core/cms/generated/api-types'
import { ButtonComponent } from 'app/shared/button/button.component'
import { SvgComponent } from 'app/shared/svg/svg.component'
import { SvgRegistry } from 'app/shared/svg/svg-registry.service'

import { CmsBlock } from '../../cms-block'
import { CmsCreditorData, createSwissBillQrCodeSvgUrl } from './swiss-bill-qr-code-util'

@Component({
  selector: 'pai-card-swiss-bill-qr-code-donation',
  templateUrl: './card-swiss-bill-qr-code-donation.component.html',
  styleUrls: ['./card-swiss-bill-qr-code-donation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SvgComponent, ButtonComponent],
  providers: [{ provide: CmsBlock, useExisting: CardSwissBillQrCodeDonationComponent }],
})
export class CardSwissBillQrCodeDonationComponent extends CmsBlock<CardSwissBillQrCodeDonation> {
  readonly __typename = 'CardSwissBillQrCodeDonation'
  private readonly clientConfig = inject(CLIENT_CONFIG)

  protected readonly currency = signal(SwissQrBillCurrency.CHF)
  protected readonly isLoading = signal(false)
  protected readonly svg = signal<string | null>(null)

  protected readonly svgTransitionEnd = new Subject<void>()

  constructor() {
    super()

    const svgUrl = computed<string>(() => {
      const { topic, creditors } = this.data()
      const currency = this.currency()
      return createSwissBillQrCodeSvgUrl(this.clientConfig.origin, creditors as CmsCreditorData, currency, topic ?? '')
    })

    effect(() => void this.loadSvgAndChangeUrl(svgUrl()))
  }

  private readonly analytics = inject(AnalyticsService)
  private readonly svgRegistry = inject(SvgRegistry)

  protected useChf() {
    this.currency.set(SwissQrBillCurrency.CHF)
    this.analytics.trackEvent(AnalyticsEvent.SWISS_BILL_QR_CODE_CARD_INTERACTION, { currency: 'chf' })
  }

  protected useEur() {
    this.currency.set(SwissQrBillCurrency.EUR)
    this.analytics.trackEvent(AnalyticsEvent.SWISS_BILL_QR_CODE_CARD_INTERACTION, { currency: 'eur' })
  }

  private async loadSvgAndChangeUrl(url: string) {
    try {
      this.isLoading.set(true)
      await Promise.all([
        this.svgRegistry.getFromUrl(url),
        // await the css transition before changing the svg to have a nice animation
        untracked(this.svg) === null ? Promise.resolve() : firstValueFrom(this.svgTransitionEnd),
      ])
      this.svg.set(url)
    } finally {
      this.isLoading.set(false)
    }
  }
}
